/**
 * Module: copilot/CopilotClient
 * Responsibility: HTTP client for the Shopflo copilot backend service.
 *   Wraps the streaming chat endpoint (SSE over fetch), the tool-confirmation
 *   endpoint, and the stream-cancel endpoint.
 * Collaborators: sseParser.ts (stream decoding), events.ts (output types),
 *   globalThis.fetch (browser or Node 18+ built-in).
 * Transport note: We use fetch + ReadableStream rather than EventSource because
 *   EventSource cannot attach an Authorization header. Bearer token auth
 *   requires full control of the request, which only fetch provides.
 */

import { parseSseStream } from './sseParser'
import type { CopilotSSEEvent } from './events'
import type { AgentKind, ChatRouting } from './agents'
import type { PlanValidationIssue, PlanView } from './plans'

/**
 * Construction options for `CopilotClient`.
 *
 * Both fields are `readonly` because they describe the identity of the client
 * instance and must not change after construction.
 */
export interface CopilotClientOptions {
  /**
   * Base URL of the copilot service, without a trailing slash.
   * @example "https://api.shopflo.co"
   */
  readonly baseUrl: string

  /**
   * Returns the current merchant's JWT access token.
   * May be sync or async — both are awaited uniformly before each request.
   * The token is passed as `Authorization: Bearer <token>`.
   */
  readonly getAccessToken: () => Promise<string> | string

  /**
   * Optional callback invoked when a backend call returns 401. It should
   * refresh the access token (e.g. POST /merchant-user/refresh and store
   * the new access token wherever `getAccessToken` reads from). After it
   * resolves, the failing request is retried ONCE with the token re-read
   * via `getAccessToken`. If the retry still returns non-2xx, that
   * second response is surfaced via the usual `CopilotError`.
   * Omit this option to keep the prior single-attempt behaviour.
   */
  readonly onUnauthorized?: () => Promise<void> | void
}

/**
 * Typed error thrown by `CopilotClient` when the server responds with a
 * non-2xx HTTP status. Carries both the HTTP `status` code and the `endpoint`
 * that was called so callers can inspect and react to specific failure modes.
 *
 * @example
 * ```typescript
 * try {
 *   await client.confirm(token, "approve");
 * } catch (err) {
 *   if (err instanceof CopilotError && err.status === 409) {
 *     // handle conflict
 *   }
 * }
 * ```
 */
export class CopilotError extends Error {
  readonly status: number
  readonly endpoint: string

  constructor(status: number, endpoint: string, message: string) {
    super(message)
    this.name = 'CopilotError'
    this.status = status
    this.endpoint = endpoint
  }
}

/**
 * `409 agent_mismatch` from POST /copilot/chat: the message @-mentioned an
 * agent other than the one that owns this conversation. A conversation never
 * changes agent, so the caller continues in a NEW conversation with
 * `handoffFrom` set to this one.
 */
export class AgentMismatchError extends CopilotError {
  readonly ownerAgent: AgentKind | undefined

  constructor(ownerAgent: AgentKind | undefined) {
    super(409, 'chat', 'copilot chat failed: 409 agent_mismatch')
    this.name = 'AgentMismatchError'
    this.ownerAgent = ownerAgent
  }
}

/** The JSON body of a non-2xx response, or null when it is not JSON. */
async function readErrorBody(res: Response): Promise<{ error?: string; agent?: AgentKind } | null> {
  try {
    return (await res.json()) as { error?: string; agent?: AgentKind }
  } catch {
    return null
  }
}

export type PlanEndpoint =
  | 'plan_approve'
  | 'plan_edit'
  | 'plan_cancel'
  | 'plan_undo'
  | 'plan_stop_monitoring'
  | 'plan_get'

/** `409 stale`: settings changed since the plan was made; the merchant asks the agent to refresh it. */
export class PlanStaleError extends CopilotError {
  readonly plan: PlanView | undefined

  constructor(endpoint: PlanEndpoint, plan: PlanView | undefined) {
    super(409, endpoint, `copilot ${endpoint} failed: 409 stale`)
    this.name = 'PlanStaleError'
    this.plan = plan
  }
}

/** `403 forbidden`: the user lacks the permission behind these items. */
export class PlanForbiddenError extends CopilotError {
  readonly lockedItemIds: readonly string[]

  constructor(endpoint: PlanEndpoint, lockedItemIds: readonly string[]) {
    super(403, endpoint, `copilot ${endpoint} failed: 403 forbidden`)
    this.name = 'PlanForbiddenError'
    this.lockedItemIds = lockedItemIds
  }
}

/** `409 conflict`: the plan moved on (wrong status or version) since this card last saw it. */
export class PlanConflictError extends CopilotError {
  readonly reason: string

  constructor(endpoint: PlanEndpoint, reason: string) {
    super(409, endpoint, `copilot ${endpoint} failed: 409 conflict`)
    this.name = 'PlanConflictError'
    this.reason = reason
  }
}

/** `422 invalid`: the edit broke a plan rule. */
export class PlanInvalidError extends CopilotError {
  readonly issues: readonly PlanValidationIssue[]

  constructor(endpoint: PlanEndpoint, issues: readonly PlanValidationIssue[]) {
    super(422, endpoint, `copilot ${endpoint} failed: 422 invalid`)
    this.name = 'PlanInvalidError'
    this.issues = issues
  }
}

interface PlanErrorBody {
  readonly error?: string
  readonly reason?: string
  readonly locked_item_ids?: readonly string[]
  readonly issues?: readonly PlanValidationIssue[]
  readonly plan?: PlanView
}

function planErrorFrom(status: number, body: PlanErrorBody | null, endpoint: PlanEndpoint): CopilotError {
  if (status === 409 && body?.error === 'stale') return new PlanStaleError(endpoint, body.plan)
  if (status === 409 && body?.error === 'conflict') return new PlanConflictError(endpoint, body.reason ?? '')
  if (status === 403 && body?.error === 'forbidden') {
    return new PlanForbiddenError(endpoint, body.locked_item_ids ?? [])
  }
  if (status === 422 && body?.error === 'invalid') return new PlanInvalidError(endpoint, body.issues ?? [])

  return new CopilotError(status, endpoint, `copilot ${endpoint} failed: ${status}`)
}

async function planError(res: Response, endpoint: PlanEndpoint): Promise<CopilotError> {
  let body: PlanErrorBody | null = null
  try {
    body = (await res.json()) as PlanErrorBody
  } catch {
    body = null
  }

  return planErrorFrom(res.status, body, endpoint)
}

/** A plan endpoint's PlanView, bare or wrapped in `{ data }` like the conversation routes. */
function unwrapPlan(body: unknown): PlanView {
  const wrapped = body as { data?: PlanView } | null
  if (wrapped && typeof wrapped === 'object' && wrapped.data && typeof wrapped.data === 'object') {
    return wrapped.data
  }

  return body as PlanView
}

/** Body and params for `POST /copilot/plans/:id/edit`. */
export interface PlanEdit {
  readonly version: number
  readonly removeItemIds?: readonly string[]
  /** itemId -> top-level input fields to change; only bounded numbers are accepted server-side. */
  readonly params?: Readonly<Record<string, Record<string, unknown>>>
}

/**
 * Client for the Shopflo copilot streaming API.
 *
 * Instantiate once per page/session and reuse across conversations. The client
 * is stateless — conversation continuity is tracked server-side by `conversationId`.
 *
 * @example
 * ```typescript
 * const client = new CopilotClient({
 *   baseUrl: "https://api.shopflo.co",
 *   getAccessToken: () => authStore.getToken(),
 * });
 *
 * for await (const event of client.chat("conv-1", "Show my top products")) {
 *   if (event.type === "delta.text") appendText(event.text);
 * }
 * ```
 */
export class CopilotClient {
  private readonly opts: CopilotClientOptions

  /**
   * Creates a new `CopilotClient`.
   *
   * @param opts - Connection and auth options. See `CopilotClientOptions`.
   */
  constructor(opts: CopilotClientOptions) {
    this.opts = opts
  }

  /**
   * Internal fetch wrapper: injects the Bearer token, and on a 401
   * response calls `onUnauthorized` (if provided), re-reads the token,
   * and retries the request ONCE. Used by every public method so the
   * refresh-on-401 behaviour stays in one place.
   *
   * Retry safety: callers MUST pass a body that is re-readable on retry
   * (a string or a fresh structured value — not a one-shot stream). All
   * current call sites pass `JSON.stringify(...)` which is safe.
   */
  private async authedFetch(url: string, init: RequestInit): Promise<Response> {
    const initialToken = await this.opts.getAccessToken()
    const baseHeaders = init.headers ?? {}

    const firstRes = await fetch(url, {
      ...init,
      headers: { ...baseHeaders, authorization: `Bearer ${initialToken}` }
    })

    if (firstRes.status !== 401 || !this.opts.onUnauthorized) {
      return firstRes
    }

    try {
      await this.opts.onUnauthorized()
    } catch {
      // Refresh threw — surface the original 401 unchanged.
      return firstRes
    }

    const refreshedToken = await this.opts.getAccessToken()
    // Don't loop: if the token didn't actually rotate, the retry would
    // 401 again — return the original response instead.
    if (!refreshedToken || refreshedToken === initialToken) {
      return firstRes
    }

    return fetch(url, {
      ...init,
      headers: { ...baseHeaders, authorization: `Bearer ${refreshedToken}` }
    })
  }

  /**
   * Sends a message to the copilot and streams back `CopilotSSEEvent` values
   * as the assistant responds, invokes tools, and completes the turn.
   *
   * The generator yields events in the order the server emits them. Callers
   * should iterate until the generator returns (stream closed) or they observe
   * a `turn.done` or `turn.error` event.
   *
   * @param conversationId - Unique ID for the conversation thread. Create a
   *   new UUID for each fresh conversation; reuse the same ID to continue an
   *   existing one.
   * @param message - The merchant's natural-language message to the copilot.
   * @param signal - Optional AbortSignal that cancels the fetch mid-stream.
   *   Useful for implementing a "Stop" button — the server-side stream is also
   *   torn down when the connection drops.
   * @param routing - Optional routing hints: the surface the merchant typed on,
   *   an @-mentioned agent, and the conversation a handoff continues.
   * @returns An async generator of `CopilotSSEEvent` values.
   * @throws `AgentMismatchError` on `409 agent_mismatch`; `CopilotError` for
   *   any other non-2xx status or a missing body.
   *
   * @example
   * ```typescript
   * const controller = new AbortController();
   * for await (const ev of client.chat("c1", "hello", controller.signal)) {
   *   if (ev.type === "delta.text") process(ev.text);
   * }
   * ```
   */
  async *chat(
    conversationId: string,
    message: string,
    signal?: AbortSignal,
    routing?: ChatRouting
  ): AsyncGenerator<CopilotSSEEvent> {
    const res = await this.authedFetch(`${this.opts.baseUrl}/copilot/chat`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream'
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        message,
        ...(routing?.surface ? { surface: routing.surface } : {}),
        ...(routing?.agent ? { agent: routing.agent } : {}),
        ...(routing?.handoffFrom ? { handoff_from: routing.handoffFrom } : {})
      }),
      signal
    })

    if (res.status === 409) {
      const body = await readErrorBody(res)
      if (body?.error === 'agent_mismatch') {
        throw new AgentMismatchError(body.agent)
      }
    }

    // Non-2xx or missing body means the server rejected or dropped the request
    // before starting the stream — surface this as a hard error.
    if (!res.ok || !res.body) {
      throw new CopilotError(res.status, 'chat', `copilot chat failed: ${res.status}`)
    }

    // Delegate stream decoding to the SSE parser. Each yielded event is
    // forwarded directly to the caller with no buffering.
    for await (const ev of parseSseStream(res.body)) {
      yield ev
    }
  }

  /**
   * Approves or rejects a pending tool confirmation.
   *
   * Call this when a `tool_confirm_required` event is received and the merchant
   * has made a decision. The server will either proceed with the tool call
   * (approve) or emit a `tool_rejected` event and continue (reject).
   *
   * @param confirmToken - The `confirm_token` from the `tool_confirm_required` event.
   * @param decision - `"approve"` to allow the tool to run; `"reject"` to cancel it.
   * @param signal - Optional AbortSignal to cancel the request mid-flight.
   * @returns Resolves when the server acknowledges the decision.
   * @throws `CopilotError` on non-2xx response. Check `err.status` for details.
   *
   * @example
   * ```typescript
   * await client.confirm(event.confirm_token, "approve");
   * ```
   */
  async confirm(confirmToken: string, decision: 'approve' | 'reject', signal?: AbortSignal): Promise<void> {
    const res = await this.authedFetch(`${this.opts.baseUrl}/copilot/chat/confirm`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ confirm_token: confirmToken, decision }),
      signal
    })

    if (!res.ok) {
      throw new CopilotError(res.status, 'confirm', `copilot confirm failed: ${res.status}`)
    }
  }

  /**
   * Cancels an in-progress copilot conversation.
   *
   * Signals the server to interrupt any ongoing tool calls or LLM generation
   * for the given conversation. The `chat()` stream may still emit a final
   * `turn.error` event after this call — callers should drain or abort the
   * generator as well.
   *
   * @param conversationId - The conversation ID originally passed to `chat()`.
   * @param signal - Optional AbortSignal to cancel the request mid-flight.
   * @returns Resolves when the server acknowledges the cancellation.
   * @throws `CopilotError` on non-2xx response. Check `err.status` for details.
   *
   * @example
   * ```typescript
   * await client.cancel("conv-1");
   * ```
   */
  async cancel(conversationId: string, signal?: AbortSignal): Promise<void> {
    const res = await this.authedFetch(`${this.opts.baseUrl}/copilot/chat/cancel`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId }),
      signal
    })

    if (!res.ok) {
      throw new CopilotError(res.status, 'cancel', `copilot cancel failed: ${res.status}`)
    }
  }

  private planUrl(planId: string, action?: string): string {
    const base = `${this.opts.baseUrl}/copilot/plans/${encodeURIComponent(planId)}`

    return action ? `${base}/${action}` : base
  }

  /** POSTs to a plan endpoint that answers with an SSE stream, yielding each PlanView it carries. */
  private async *planStream(
    url: string,
    endpoint: PlanEndpoint,
    body: unknown,
    signal?: AbortSignal
  ): AsyncGenerator<PlanView> {
    const res = await this.authedFetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal
    })

    if (!res.ok) throw await planError(res, endpoint)
    if (!res.body) throw new CopilotError(res.status, endpoint, `copilot ${endpoint} failed: empty stream`)

    for await (const ev of parseSseStream(res.body)) {
      if (ev.type === 'plan.error') throw planErrorFrom(ev.status, ev, endpoint)
      if (ev.type === 'plan.updated' || ev.type === 'plan.proposed') yield ev.plan
    }
  }

  private async planJson(
    url: string,
    endpoint: PlanEndpoint,
    init: { readonly method: 'GET' | 'POST'; readonly body?: unknown },
    signal?: AbortSignal
  ): Promise<PlanView> {
    const res = await this.authedFetch(url, {
      method: init.method,
      headers: { 'content-type': 'application/json' },
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      signal
    })

    if (!res.ok) throw await planError(res, endpoint)

    return unwrapPlan(await res.json())
  }

  /**
   * Approves a PENDING plan at `version` and streams its progress as the
   * engine makes each change.
   *
   * @throws `PlanStaleError`, `PlanForbiddenError`, `PlanConflictError`,
   *   `PlanInvalidError`, or `CopilotError` (404 and anything else) before the stream starts.
   */
  approvePlan(planId: string, version: number, signal?: AbortSignal): AsyncGenerator<PlanView> {
    return this.planStream(this.planUrl(planId, 'approve'), 'plan_approve', { version }, signal)
  }

  /** Reverts all of a plan's changes, or the listed items, streaming progress like `approvePlan`. */
  undoPlan(planId: string, itemIds: readonly string[] | 'all', signal?: AbortSignal): AsyncGenerator<PlanView> {
    return this.planStream(this.planUrl(planId, 'undo'), 'plan_undo', { item_ids: itemIds }, signal)
  }

  /** Removes items (dependents go too, server-side) or changes bounded numbers. Bumps `version`. */
  editPlan(planId: string, edit: PlanEdit, signal?: AbortSignal): Promise<PlanView> {
    const body = {
      version: edit.version,
      ...(edit.removeItemIds ? { remove_item_ids: edit.removeItemIds } : {}),
      ...(edit.params ? { params: edit.params } : {})
    }

    return this.planJson(this.planUrl(planId, 'edit'), 'plan_edit', { method: 'POST', body }, signal)
  }

  cancelPlan(planId: string, signal?: AbortSignal): Promise<PlanView> {
    return this.planJson(this.planUrl(planId, 'cancel'), 'plan_cancel', { method: 'POST', body: {} }, signal)
  }

  /** Keeps the changes and ends automatic actions. */
  stopPlanMonitoring(planId: string, signal?: AbortSignal): Promise<PlanView> {
    return this.planJson(
      this.planUrl(planId, 'stop-monitoring'),
      'plan_stop_monitoring',
      { method: 'POST', body: {} },
      signal
    )
  }

  getPlan(planId: string, signal?: AbortSignal): Promise<PlanView> {
    return this.planJson(this.planUrl(planId), 'plan_get', { method: 'GET' }, signal)
  }
}
